import AudioRecordingModal from "@/components/design-system/AudioRecordingModal";
import { Button } from "@/components/design-system/button";
import Modal from "@/components/design-system/Modal";
import VideoRecordingModal from "@/components/design-system/VideoRecordingModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DocumentSVG, MicSVG, PDFSVG, VideoSVG } from "@/utils/svg";
import { FileVideo2, Mic, Trash2 } from "lucide-react";
import React, { useState, forwardRef, useImperativeHandle } from "react";

interface RecordOptionProps {
  selectedFile?: File | null;
  selectedRecordedFile?: File | null;
  fileType?: string;
  onRecordingComplete?: (file: File, type: string) => void;
  handleDeleteRecordedFile?: () => void;
  disabled?: boolean;
}

// Separate component for recording audio/video
const RecordOption: React.FC<RecordOptionProps> = ({
  selectedFile,
  selectedRecordedFile,
  fileType,
  onRecordingComplete,
  handleDeleteRecordedFile,
  disabled = false,
}) => {
  // We don't need isRecording state here since modals handle their own recording state
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

  const handleRecordStart = (type: "audio" | "video") => {
    if (type === "audio") {
      setShowAudioModal(true);
    } else if (type === "video") {
      setShowVideoModal(true);
    }
  };

  // Handle audio recording completion
  const handleAudioSave = (file: File) => {
    if (onRecordingComplete) {
      onRecordingComplete(file, "audio");
    }
  };

  // Handle video recording completion
  const handleVideoSave = (file: File) => {
    if (onRecordingComplete) {
      onRecordingComplete(file, "video");
    }
  };

  return (
    <>
      {selectedRecordedFile ? (
        <div className="flex items-center h-[38px] border border-[#2165C5] rounded-xl px-3 text-[#1F1F1F] bg-white">
          <div className="flex items-center gap-2">
            {fileType === "video" && (
              <FileVideo2 className="w-4 h-4 text-[#2165C5]" />
            )}
            {fileType === "audio" && <Mic className="w-4 h-4 text-[#2165C5]" />}

            <span
              className="text-sm truncate max-w-[100px]"
              title={selectedRecordedFile?.name}
            >
              {selectedRecordedFile?.name}
            </span>
          </div>
          {disabled ? null : (
            <button
              type="button"
              onClick={() => setShowConfirmDeleteModal(true)}
              className="ml-3 text-[#FF152C] hover:text-red-700"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              className="h-[38px] w-[168px] text-[#5C88C5]"
              variant="primary-outline"
              disabled={Boolean(selectedFile)}
            >
              Record
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[180px] bg-white">
            <DropdownMenuItem
              className="flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group border-b rounded-none border-[#EDEDED]"
              onClick={() => handleRecordStart("audio")}
            >
              <div className="flex items-center gap-2 w-full">
                <MicSVG />
                <span>Record an Audio</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group rounded-none"
              onClick={() => handleRecordStart("video")}
            >
              <div className="flex items-center gap-2 w-full">
                <VideoSVG />
                <span>Record a Video</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Modal
        isOpen={showConfirmDeleteModal}
        onClose={() => setShowConfirmDeleteModal(false)}
        title="Delete File"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure you want to delete the file?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setShowConfirmDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => {
              handleDeleteRecordedFile?.();
              setShowConfirmDeleteModal(false);
            }}
          >
            Yes, Delete
          </Button>
        </div>
      </Modal>

      {/* Audio Recording Modal */}
      <Modal
        isOpen={showAudioModal}
        onClose={() => setShowAudioModal(false)}
        title="Add Audio Submissions"
        width="976px"
        contentClassName="p-0"
      >
        {showAudioModal && (
          <AudioRecordingModal
            onSave={handleAudioSave}
            onClose={() => setShowAudioModal(false)}
          />
        )}
      </Modal>

      {/* Video Recording Modal */}
      <Modal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        title="Add Video Submissions"
        width="976px"
        contentClassName="p-0"
      >
        {showVideoModal && (
          <VideoRecordingModal
            onSave={handleVideoSave}
            onClose={() => setShowVideoModal(false)}
          />
        )}
      </Modal>
    </>
  );
};

interface FileUploadSectionProps {
  showLabel?: boolean;
  required?: boolean;
  onFileUpload: (files: File | null) => void;
  disabled?: boolean;
}

// Define the ref interface to expose methods
export interface FileUploadSectionRef {
  clearSelectedFile: () => void;
  clearSelectedRecordedFile: () => void;
}

// Constants for file size limits (in bytes)
const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB
const MAX_DEFAULT_SIZE = 10 * 1024 * 1024; // 10MB for all other file types

const FileUploadSection = forwardRef<
  FileUploadSectionRef,
  FileUploadSectionProps
>((props, ref) => {
  const {
    showLabel = true,
    required = false,
    onFileUpload,
    disabled = false,
  } = props;
  // State to track selected file for display
  const [selectedRecordedFile, setSelectedRecordedFile] = useState<File | null>(
    null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<string>("");
  const [sizeError, setSizeError] = useState<string>("");
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

  // Handle file selection
  const handleFileSelect = (type: string) => {
    // Create a file input element
    const fileInput = document.createElement("input");
    fileInput.type = "file";

    // Set file type for UI display
    setFileType(type);

    // Set accepted file types based on the selected type
    switch (type) {
      case "audio":
        fileInput.accept = "audio/*";
        break;
      case "video":
        fileInput.accept = "video/*";
        break;
      case "pdf":
        fileInput.accept = ".pdf";
        break;
      case "docx":
        fileInput.accept = ".doc,.docx";
        break;
      default:
        fileInput.accept = "*/*";
    }

    // Handle file selection
    fileInput.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        const file = files[0];

        // Check file size based on type
        if (type === "video" && file.size > MAX_VIDEO_SIZE) {
          setSizeError("Video file size exceeds the 50MB limit");
          return;
        }

        if (type !== "video" && file.size > MAX_DEFAULT_SIZE) {
          setSizeError(`File size exceeds the 10MB limit`);
          return;
        }

        // Clear any previous errors
        setSizeError("");

        // Store the selected file for UI display
        setSelectedFile(file);
        onFileUpload(file);
      }
    };

    // Trigger file selection dialog
    fileInput.click();
  };

  // Handle file deletion
  const handleDeleteFile = () => {
    setSelectedFile(null);
    setSizeError(""); // Clear any error messages
    // Notify parent component that file was removed
    // This assumes your parent component can handle file removal
    // You may need to modify onFileUpload to handle removal as well
    onFileUpload(null);
  };

  const handleDeleteRecordedFile = () => {
    setSelectedRecordedFile(null);
    setSizeError(""); // Clear any error messages
    // Notify parent component that file was removed
    // This assumes your parent component can handle file removal
    // You may need to modify onFileUpload to handle removal as well
    onFileUpload(null);
  };

  // Expose methods to parent component through ref
  useImperativeHandle(ref, () => ({
    clearSelectedFile: () => {
      setSelectedFile(null);
      setSizeError("");
    },
    clearSelectedRecordedFile: () => {
      setSelectedRecordedFile(null);
      setSizeError("");
    },
  }));

  return (
    <div className="mb-8">
      {showLabel && (
        <h2 className="text-[#1F1F1F] text-[20px] font-semibold mb-6">
          File Upload
          {required && <span className="text-[#ef4444] ml-1">*</span>}
        </h2>
      )}

      <div className="flex items-center gap-3">
        <RecordOption
          fileType={fileType}
          selectedFile={selectedFile}
          disabled={disabled}
          selectedRecordedFile={selectedRecordedFile}
          handleDeleteRecordedFile={handleDeleteRecordedFile}
          onRecordingComplete={(file, type) => {
            // Check file size based on type
            if (type === "video" && file.size > MAX_VIDEO_SIZE) {
              setSizeError("Video file size exceeds the 50MB limit");
              return;
            }

            if (type !== "video" && file.size > MAX_DEFAULT_SIZE) {
              setSizeError(`File size exceeds the 10MB limit`);
              return;
            }

            // Clear any previous errors
            setSizeError("");

            setSelectedRecordedFile(file);
            setFileType(type);
            onFileUpload(file);
          }}
        />
        {selectedFile ? (
          <div className="flex items-center h-[38px] border border-[#2165C5] rounded-xl px-3 text-[#1F1F1F] bg-white">
            <div className="flex items-center gap-2">
              {fileType === "video" && (
                <VideoSVG className="w-4 h-4 text-[#2165C5]" />
              )}
              {fileType === "audio" && (
                <MicSVG className="w-4 h-4 text-[#2165C5]" />
              )}
              {fileType === "pdf" && (
                <PDFSVG className="w-4 h-4 text-[#2165C5]" />
              )}
              {fileType === "docx" && (
                <DocumentSVG className="w-4 h-4 text-[#2165C5]" />
              )}
              <span
                className="text-sm truncate max-w-[100px]"
                title={selectedFile.name}
              >
                {selectedFile.name}
              </span>
            </div>
            {disabled ? null : (
              <button
                type="button"
                onClick={() => setShowConfirmDeleteModal(true)}
                className="ml-3 text-[#FF152C] hover:text-red-700"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                className="h-[38px] w-[168px] text-[#5C88C5]"
                variant="primary-outline"
                disabled={Boolean(selectedRecordedFile)}
              >
                Upload
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px] bg-white">
              <DropdownMenuItem
                className="flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group border-b rounded-none border-[#EDEDED]"
                onClick={() => handleFileSelect("audio")}
              >
                <div className="flex items-center gap-2 w-full">
                  {/* <Mic className="w-4 h-4 text-[#1F1F1F] group-hover:text-white" /> */}
                  <MicSVG />
                  <span>Audio</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group border-b rounded-none border-[#EDEDED]"
                onClick={() => handleFileSelect("video")}
              >
                <div className="flex items-center gap-2 w-full">
                  {/* <FileVideo2 className="w-4 h-4 text-[#1F1F1F] group-hover:text-white" /> */}
                  <VideoSVG />
                  <span>Video</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group border-b rounded-none border-[#EDEDED]"
                onClick={() => handleFileSelect("pdf")}
              >
                <div className="flex items-center gap-2 w-full">
                  {/* <File className="w-4 h-4 text-[#1F1F1F] group-hover:text-white" /> */}
                  <PDFSVG />
                  <span>PDF</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center gap-2 text-label2a-d text-borderBlack cursor-pointer hover:bg-[#5C88C5] hover:text-white group rounded-none"
                onClick={() => handleFileSelect("docx")}
              >
                <div className="flex items-center gap-2 w-full">
                  <DocumentSVG />
                  <span>DOCX</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <Modal
        isOpen={showConfirmDeleteModal}
        onClose={() => setShowConfirmDeleteModal(false)}
        title="Delete File"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure you want to delete the file?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setShowConfirmDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => {
              handleDeleteFile?.();
              setShowConfirmDeleteModal(false);
            }}
          >
            Yes, Delete
          </Button>
        </div>
      </Modal>

      {sizeError && (
        <div className="text-[#ef4444] text-sm mb-2">{sizeError}</div>
      )}
    </div>
  );
});

export default FileUploadSection;
